import { Col, Row } from "antd";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdDownload,
  MdForwardToInbox,
  MdOutlineAlternateEmail,
  MdOutlineBadge,
  MdOutlineCall,
  MdReportGmailerrorred,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import InsurancePlan from "./InsurancePlan";
import validation from "./validation";

const Initial = ({
  combinations,
  onSendQuote,
  onDownload,
  onCheck,
  onJustify,
  justifications,
  selectedCombinationIds,
  consumedCombinationKeys = [],
  reachedMaxCombinations = false,
  translation,
  insurancePlans = [],
  addLinks,
  setAddLinks,
  onCopyLink,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });

  const { submitForm, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  const handleDownload = () => onDownload(submitForm);
  const handleSendQuote = () => onSendQuote(submitForm);

  return (
    <div className="grid--1">
      <div>
        <div className="download__header">{translation.TITLE}</div>
      </div>
      <div className="grid--1">
        <Alert
          type="info"
          showIcon
          message={
            "Sélectionnez les combinaisons (Socle avec Confort) à inclure dans le devis. Le prospect choisira ensuite la combinaison qu'il souhaite signer."
          }
          icon={<MdReportGmailerrorred size={16} />}
        />
        {combinations.map((combination) => {
          const key = `${get(combination, "baseInsurancePlan.id")}|${
            get(combination, "complementaryInsurancePlan.id") || "none"
          }`;
          const isConsumed = consumedCombinationKeys.includes(key);
          return (
            <div key={combination.id}>
              <InsurancePlan
                onCheck={(state) => onCheck(combination.id, state)}
                checked={
                  isConsumed || selectedCombinationIds.includes(combination.id)
                }
                logo={get(combination, "baseInsurancePlan.icon")}
                name={combination.label}
                description={get(combination, "baseInsurancePlan.description")}
                cost={combination.totalCost}
                currency={combination.currency}
                translation={translation}
                justification={justifications[combination.id]}
                onJustify={(justification) =>
                  onJustify(justification, combination.id)
                }
                checkDisabled={reachedMaxCombinations}
                disabled={isConsumed}
                baseName={get(combination, "baseInsurancePlan.name")}
                baseCost={combination.baseCost}
                complementName={get(
                  combination,
                  "complementaryInsurancePlan.name",
                )}
                complementCost={combination.complementCost}
              />
            </div>
          );
        })}
      </div>

      <div className="subscription-form--wrapper">
        <Form
          type="vertical"
          title="Informations de contact"
          subtitle="Renseignez vos coordonnées pour l'envoi de votre devis"
        >
          <Row gutter={[16, 16]}>
            <Col lg={12} xs={24}>
              <Input
                placeholder="Ex : Bernard"
                label="Prénom"
                icon={<MdOutlineBadge size={16} />}
                {...bindInputProps({
                  name: "contact.user.firstname",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue("contact.user.firstname", value);
                }}
              />
            </Col>
            <Col lg={12} xs={24}>
              <Input
                label="Nom"
                icon={<MdOutlineBadge size={16} />}
                placeholder="Ex : Doe"
                {...bindInputProps({
                  name: "contact.user.lastname",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue("contact.user.lastname", value);
                }}
              />
            </Col>
            <Col lg={12} xs={24}>
              <Input
                label="N° de téléphone"
                icon={<MdOutlineCall size={16} />}
                placeholder="Ex : 06 10 20 30 40"
                {...bindInputProps({
                  name: `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue(
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                    value,
                  );
                }}
              />
            </Col>
            <Col lg={12} xs={24}>
              <Input
                label="Adresse email"
                icon={<MdOutlineAlternateEmail size={16} />}
                placeholder="Ex : exemple@domaine.fr"
                {...bindInputProps({
                  name: `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue(
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                    value,
                  );
                }}
              />
            </Col>
          </Row>
        </Form>
      </div>

      <div className="download__actions">
        <div className="download__actions--container">
          <Button
            onClick={handleDownload}
            disabled={
              !selectedCombinationIds.length ||
              !isEmpty(formProps.errors) ||
              !get(values, "contact.user.firstname") ||
              !get(values, "contact.user.lastname") ||
              !get(
                values,
                `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
              ) ||
              !get(
                values,
                `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
              )
            }
            type="primary--outlined"
            size="full--width"
          >
            {translation.DOWNLOAD}
            <MdDownload size={16} />
          </Button>
        </div>
        <span className="download__actions--or">{translation.OR}</span>
        <div className="download__actions--container">
          <Button
            onClick={handleSendQuote}
            disabled={
              !selectedCombinationIds.length || !isEmpty(formProps.errors)
            }
            type="primary"
            size="full--width"
          >
            {translation.SEND_BY_EMAIL}
            <MdForwardToInbox size={16} />
          </Button>

          <Input
            type="checkbox"
            label={translation.ADD_LINKS}
            checked={addLinks}
            onChange={({ target: { checked } }) => {
              setAddLinks(checked);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Initial;
